import { LoadingStatus } from "./activity";
import { formatRupiah } from "@/lib/display-format";
import { useEffect, useMemo, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import {
  ArrowLeft,
  CalendarDays,
  Download,
  Eye,
  FileArchive,
  FileSearch,
  Filter,
  HardDrive,
  LoaderCircle,
  Search,
  ShieldAlert,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  deleteImportSources,
  downloadImportSource,
  loadRemoteSourceRows,
  previewImportSourceDeletion,
  type ImportSource,
  type ImportSourceRowDetail,
  type SourceDeletionPreview,
} from "@/lib/supabase-repository";
import type { Language } from "@/lib/finance";
import type { Transaction } from "@/lib/finance";
import type { BusinessUnit } from "@/lib/business-units";
import type { FinanceCategory } from "@/lib/categories";
import type { FinancialAccount } from "@/lib/profit-loss";
import type { AppRole } from "./auth";
import { TransactionDetailPreview } from "./transaction-detail-preview";

type LibraryMode = "list" | "detail" | "delete";

type SourceLibraryProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sources: ImportSource[];
  language: Language;
  role: AppRole;
  client: SupabaseClient;
  transactions: Transaction[];
  businessUnits: BusinessUnit[];
  categories: FinanceCategory[];
  accounts: FinancialAccount[];
  onIsolate: (sourceIds: string[]) => void;
  onDeleted: (sourceIds: string[]) => Promise<void>;
};

function formatDate(value: string, language: Language) {
  return new Intl.DateTimeFormat(language === "id" ? "id-ID" : "en-US", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function formatSize(value: number | null, language: Language) {
  if (value === null) return language === "id" ? "Tidak tersedia" : "Unavailable";
  return new Intl.NumberFormat(language === "id" ? "id-ID" : "en-US", {
    style: "unit",
    unit: value >= 1_048_576 ? "megabyte" : "kilobyte",
    maximumFractionDigits: 1,
  }).format(value >= 1_048_576 ? value / 1_048_576 : value / 1_024);
}

function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function payloadText(payload: Record<string, unknown>, key: string) {
  const value = payload[key];
  return value === null || value === undefined || value === "" ? "—" : String(value);
}

function payloadAmount(payload: Record<string, unknown>, language: Language) {
  const value = Number(payload.amount_idr || 0);
  return value
    ? formatRupiah(value, language)
    : "—";
}

export function SourceLibrary({
  open,
  onOpenChange,
  sources,
  language,
  role,
  client,
  transactions,
  businessUnits,
  categories,
  accounts,
  onIsolate,
  onDeleted,
}: SourceLibraryProps) {
  const [mode, setMode] = useState<LibraryMode>("list");
  const [search, setSearch] = useState("");
  const [format, setFormat] = useState("all");
  const [sort, setSort] = useState("newest");
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [detailSource, setDetailSource] = useState<ImportSource | null>(null);
  const [detailRows, setDetailRows] = useState<ImportSourceRowDetail[]>([]);
  const [detailPage, setDetailPage] = useState(1);
  const [detailBusy, setDetailBusy] = useState(false);
  const [deleteIds, setDeleteIds] = useState<string[]>([]);
  const [deletePreview, setDeletePreview] = useState<SourceDeletionPreview | null>(null);
  const [deleteExclusive, setDeleteExclusive] = useState(true);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [downloadBusyId, setDownloadBusyId] = useState("");
  const [detailTransaction, setDetailTransaction] = useState<Transaction | null>(null);
  const canDownload = role === "admin";
  const canDelete = role === "admin";
  const text = language === "id" ? {
    title: "Sumber Impor",
    description: "Cari, periksa, isolasi, unduh, atau hapus file yang pernah diimpor.",
    search: "Cari nama file, pengimpor, atau hash",
    allFormats: "Semua format",
    newest: "Terbaru",
    oldest: "Terlama",
    fileName: "Nama file",
    importedAt: "Waktu impor",
    rows: "Baris",
    inserted: "Baru",
    duplicates: "Duplikat",
    excluded: "Tidak diimpor",
    original: "File asli",
    available: "Tersedia",
    unavailable: "Tidak tersimpan",
    selectPage: "Pilih semua di halaman",
    isolate: "Tampilkan transaksi",
    deleteSelected: "Hapus sumber terpilih",
    noSource: "Belum ada sumber impor yang cocok.",
    details: "Detail sumber",
    rowsFromSource: "Baris hasil pembacaan",
    importedBy: "Diimpor oleh",
    fileSize: "Ukuran file",
    fileHash: "SHA-256",
    download: "Unduh asli",
    back: "Kembali ke daftar",
    deleteTitle: "Hapus sumber impor",
    deleteDescription: "Tinjau dampaknya sebelum data dihapus secara permanen.",
    selectedFiles: "file terpilih",
    affected: "transaksi terhubung",
    exclusive: "transaksi hanya berasal dari sumber terpilih",
    shared: "transaksi juga memiliki sumber lain dan akan dipertahankan",
    originals: "file asli akan dihapus dari Storage",
    keepTransactions: "Hapus sumber saja; pertahankan seluruh transaksi",
    deleteTransactions: "Hapus sumber dan transaksi yang tidak memiliki sumber lain",
    typeDelete: "Ketik HAPUS untuk mengonfirmasi",
    deleting: "Menghapus…",
    confirmDelete: "Hapus secara permanen",
    deleted: "Sumber berhasil dihapus",
    raw: "Lihat payload asli",
    transactionDetails: "Lihat detail transaksi",
  } : {
    title: "Import Sources",
    description: "Search, inspect, isolate, download, or delete previously imported files.",
    search: "Search filename, importer, or hash",
    allFormats: "All formats",
    newest: "Newest",
    oldest: "Oldest",
    fileName: "Filename",
    importedAt: "Imported at",
    rows: "Rows",
    inserted: "New",
    duplicates: "Duplicates",
    excluded: "Excluded",
    original: "Original file",
    available: "Available",
    unavailable: "Not stored",
    selectPage: "Select page",
    isolate: "Show transactions",
    deleteSelected: "Delete selected sources",
    noSource: "No matching import sources.",
    details: "Source details",
    rowsFromSource: "Parsed source rows",
    importedBy: "Imported by",
    fileSize: "File size",
    fileHash: "SHA-256",
    download: "Download original",
    back: "Back to list",
    deleteTitle: "Delete import sources",
    deleteDescription: "Review the impact before permanently deleting data.",
    selectedFiles: "selected files",
    affected: "linked transactions",
    exclusive: "transactions only belong to the selected sources",
    shared: "transactions also have another source and will be retained",
    originals: "original files will be removed from Storage",
    keepTransactions: "Delete sources only; keep every transaction",
    deleteTransactions: "Delete sources and transactions with no other source",
    typeDelete: "Type DELETE to confirm",
    deleting: "Deleting…",
    confirmDelete: "Delete permanently",
    deleted: "Sources deleted",
    raw: "View raw payload",
    transactionDetails: "View transaction details",
  };
  const requiredConfirmation = language === "id" ? "HAPUS" : "DELETE";

  useEffect(() => {
    if (!open) {
      setMode("list");
      setDetailSource(null);
      setDeletePreview(null);
      setDeleteConfirmation("");
      setDetailTransaction(null);
    }
  }, [open]);

  const formats = useMemo(() => [...new Set(sources.map((source) => source.fileFormat.toLowerCase()))].sort(), [sources]);
  const transactionById = useMemo(() => new Map((open ? transactions : []).map((transaction) => [transaction.id, transaction])), [open, transactions]);
  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return [...sources]
      .filter((source) => format === "all" || source.fileFormat.toLowerCase() === format)
      .filter((source) => !query || [source.fileName, source.importedByName, source.fileSha256]
        .join(" ").toLowerCase().includes(query))
      .sort((a, b) => sort === "oldest"
        ? a.createdAt.localeCompare(b.createdAt)
        : b.createdAt.localeCompare(a.createdAt));
  }, [sources, search, format, sort]);
  const pageSize = 8;
  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, pages);
  const visibleSources = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  useEffect(() => setPage(1), [search, format, sort]);
  useEffect(() => {
    const valid = new Set(sources.map((source) => source.id));
    setSelectedIds((current) => current.filter((id) => valid.has(id)));
  }, [sources]);

  const showDetails = async (source: ImportSource) => {
    setDetailSource(source);
    setDetailRows([]);
    setDetailPage(1);
    setMode("detail");
    setDetailBusy(true);
    try {
      setDetailRows(await loadRemoteSourceRows(client, source.id));
    } catch {
      toast.error(language === "id" ? "Baris sumber gagal dimuat." : "Source rows could not be loaded.");
    } finally {
      setDetailBusy(false);
    }
  };

  const downloadSource = async (source: ImportSource) => {
    if (!canDownload || !source.storagePath) return;
    setDownloadBusyId(source.id);
    try {
      saveBlob(await downloadImportSource(client, source), source.fileName);
    } catch {
      toast.error(language === "id" ? "File asli gagal diunduh." : "The original file could not be downloaded.");
    } finally {
      setDownloadBusyId("");
    }
  };

  const openDelete = async (ids: string[]) => {
    if (!canDelete || !ids.length) return;
    setDeleteIds(ids);
    setDeletePreview(null);
    setDeleteConfirmation("");
    setDeleteExclusive(true);
    setMode("delete");
    try {
      setDeletePreview(await previewImportSourceDeletion(client, ids));
    } catch {
      toast.error(language === "id" ? "Dampak penghapusan gagal dihitung." : "Deletion impact could not be calculated.");
      setMode("list");
    }
  };

  const confirmDelete = async () => {
    if (!deletePreview || deleteConfirmation !== requiredConfirmation) return;
    setDeleteBusy(true);
    try {
      const result = await deleteImportSources(client, deleteIds, deleteExclusive);
      setSelectedIds([]);
      setMode("list");
      try {
        await onDeleted(deleteIds);
      } catch {
        onOpenChange(false);
        toast.warning(language === "id"
          ? "Sumber sudah dihapus, tetapi tampilan belum dapat dimuat ulang. Muat ulang halaman."
          : "The sources were deleted, but the view could not refresh. Reload the page.");
        return;
      }
      toast.success(`${text.deleted}: ${result.deletedBatchCount} · ${result.deletedTransactions} ${language === "id" ? "transaksi dihapus" : "transactions deleted"}`);
      if (result.storageCleanupFailed) toast.warning(language === "id" ? "Metadata sudah dihapus, tetapi sebagian file Storage perlu dibersihkan manual." : "Metadata was deleted, but some Storage files require manual cleanup.");
    } catch {
      toast.error(language === "id" ? "Sumber gagal dihapus. Data lama tidak diubah." : "Sources could not be deleted. Existing data was not changed.");
    } finally {
      setDeleteBusy(false);
    }
  };

  const toggleSource = (id: string) => {
    setSelectedIds((current) => current.includes(id)
      ? current.filter((value) => value !== id)
      : [...current, id]);
  };
  const visibleIds = visibleSources.map((source) => source.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));
  const toggleVisible = () => setSelectedIds((current) => allVisibleSelected
    ? current.filter((id) => !visibleIds.includes(id))
    : [...new Set([...current, ...visibleIds])]);

  const detailPageSize = 10;
  const detailPages = Math.max(1, Math.ceil(detailRows.length / detailPageSize));
  const detailSafePage = Math.min(detailPage, detailPages);
  const visibleDetailRows = detailRows.slice((detailSafePage - 1) * detailPageSize, detailSafePage * detailPageSize);

  return (
    <><Sheet open={open} onOpenChange={(next) => { if (!next) setDetailTransaction(null); onOpenChange(next); }}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-4xl">
        {mode === "list" && <>
          <SheetHeader className="border-b px-4 py-5 sm:px-6">
            <SheetTitle className="flex items-center gap-2"><FileArchive className="size-5" />{text.title}</SheetTitle>
            <SheetDescription>{text.description}</SheetDescription>
          </SheetHeader>
          <div className="space-y-4 p-4 sm:p-6">
            <p className="text-xs leading-5 text-muted-foreground">{language === "id"
              ? "Mulai v0.19.6, duplikat dilewati dan tidak ditautkan ke transaksi lama. Angka duplikat pada impor terdahulu tetap mengikuti keputusan saat itu."
              : "From v0.19.6, duplicates are skipped without linking existing transactions. Earlier import duplicate counts retain their original meaning."}</p>
            <div className="grid gap-3 md:grid-cols-[1fr_160px_160px]">
              <div className="relative"><Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" /><Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder={text.search} className="min-h-10 pl-9" /></div>
              <Select value={format} onValueChange={setFormat}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">{text.allFormats}</SelectItem>{formats.map((value) => <SelectItem key={value} value={value}>{value.toUpperCase()}</SelectItem>)}</SelectContent></Select>
              <Select value={sort} onValueChange={setSort}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="newest">{text.newest}</SelectItem><SelectItem value="oldest">{text.oldest}</SelectItem></SelectContent></Select>
            </div>

            <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center">
              <Button variant="outline" size="sm" className="min-h-10 sm:min-h-8" onClick={toggleVisible} disabled={!visibleSources.length}><input type="checkbox" readOnly checked={allVisibleSelected} className="size-4 accent-fuchsia-600" />{text.selectPage}</Button>
              <Badge variant="secondary" className="rounded-full">{selectedIds.length} / {sources.length}</Badge>
              <Button size="sm" variant="outline" className="min-h-10 sm:min-h-8" disabled={!selectedIds.length} onClick={() => { onIsolate(selectedIds); onOpenChange(false); }}><Filter />{text.isolate}</Button>
              {canDelete && <Button size="sm" variant="destructive" className="min-h-10 sm:ml-auto sm:min-h-8" disabled={!selectedIds.length} onClick={() => void openDelete(selectedIds)}><Trash2 />{text.deleteSelected}</Button>}
            </div>

            <div className="hidden overflow-x-auto rounded-2xl border md:block">
              <Table className="min-w-[860px]">
                <TableHeader><TableRow className="bg-muted/40"><TableHead className="w-10" /><TableHead>{text.fileName}</TableHead><TableHead>{text.importedAt}</TableHead><TableHead className="text-right">{text.rows}</TableHead><TableHead className="text-right">{text.inserted}</TableHead><TableHead className="text-right">{text.duplicates}</TableHead><TableHead className="text-right">{text.excluded}</TableHead><TableHead>{text.original}</TableHead><TableHead className="w-32" /></TableRow></TableHeader>
                <TableBody>
                  {visibleSources.map((source) => <TableRow key={source.id}>
                    <TableCell><input type="checkbox" checked={selectedIds.includes(source.id)} onChange={() => toggleSource(source.id)} aria-label={`${text.fileName}: ${source.fileName}`} className="size-4 accent-fuchsia-600" /></TableCell>
                    <TableCell><button className="max-w-[260px] text-left" onClick={() => void showDetails(source)}><span className="block truncate font-medium" title={source.fileName}>{source.fileName}</span><span className="mt-1 block text-xs uppercase text-muted-foreground">{source.fileFormat} · {source.importedByName}</span></button></TableCell>
                    <TableCell className="text-sm text-muted-foreground">{formatDate(source.createdAt, language)}</TableCell>
                    <TableCell className="text-right tabular-nums">{(source.detectedRowCount ?? source.rowCount)}</TableCell>
                    <TableCell className="text-right tabular-nums text-emerald-700 dark:text-emerald-400">{source.insertedCount}</TableCell>
                    <TableCell className="text-right tabular-nums">{source.duplicateCount}</TableCell>
                    <TableCell className="text-right tabular-nums text-red-700 dark:text-red-300">{source.excludedCount ?? "—"}</TableCell>
                    <TableCell>{source.storagePath ? <Badge variant="outline" className="border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"><HardDrive />{text.available}</Badge> : <Badge variant="outline" className="text-muted-foreground">{text.unavailable}</Badge>}</TableCell>
                    <TableCell><div className="flex justify-end gap-1"><Button variant="ghost" size="icon-sm" onClick={() => void showDetails(source)} aria-label={text.details}><Eye /></Button>{canDownload && <Button variant="ghost" size="icon-sm" disabled={!source.storagePath || downloadBusyId === source.id} onClick={() => void downloadSource(source)} aria-label={text.download}>{downloadBusyId === source.id ? <LoaderCircle className="animate-spin" /> : <Download />}</Button>}</div></TableCell>
                  </TableRow>)}
                  {!visibleSources.length && <TableRow><TableCell colSpan={9}><div className="flex min-h-40 flex-col items-center justify-center text-center text-muted-foreground"><FileSearch className="mb-3 size-7 opacity-40" /><p className="text-sm">{text.noSource}</p></div></TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
            <div className="space-y-3 md:hidden">
              {visibleSources.map((source) => <article key={`${source.id}-mobile`} className="rounded-2xl border bg-card p-4 shadow-sm">
                <div className="flex items-start gap-3"><input type="checkbox" checked={selectedIds.includes(source.id)} onChange={() => toggleSource(source.id)} aria-label={`${text.fileName}: ${source.fileName}`} className="mt-1 size-5 shrink-0 accent-fuchsia-600" /><button className="min-w-0 flex-1 text-left" onClick={() => void showDetails(source)}><span className="block break-words text-sm font-semibold">{source.fileName}</span><span className="mt-1 block text-xs uppercase text-muted-foreground">{source.fileFormat} · {source.importedByName}</span></button></div>
                <div className="mt-3 grid grid-cols-2 gap-2 rounded-xl bg-muted/25 p-3 text-xs"><span><span className="block text-muted-foreground">{text.importedAt}</span><span className="mt-1 block font-medium">{formatDate(source.createdAt, language)}</span></span><span><span className="block text-muted-foreground">{text.rows}</span><span className="mt-1 block font-medium tabular-nums">{source.detectedRowCount ?? source.rowCount} · {source.insertedCount} {text.inserted.toLowerCase()} · {source.excludedCount ?? "—"} {text.excluded.toLowerCase()}</span></span></div>
                <div className="mt-3 flex items-center justify-between gap-2">{source.storagePath ? <Badge variant="outline" className="border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"><HardDrive />{text.available}</Badge> : <Badge variant="outline" className="text-muted-foreground">{text.unavailable}</Badge>}<div className="flex gap-2"><Button variant="outline" size="icon-lg" onClick={() => void showDetails(source)} aria-label={text.details}><Eye /></Button>{canDownload && <Button variant="outline" size="icon-lg" disabled={!source.storagePath || downloadBusyId === source.id} onClick={() => void downloadSource(source)} aria-label={text.download}>{downloadBusyId === source.id ? <LoaderCircle className="animate-spin" /> : <Download />}</Button>}</div></div>
              </article>)}
              {!visibleSources.length && <div className="flex min-h-40 flex-col items-center justify-center rounded-2xl border text-center text-muted-foreground"><FileSearch className="mb-3 size-7 opacity-40" /><p className="text-sm">{text.noSource}</p></div>}
            </div>
            <div className="flex items-center justify-between text-sm text-muted-foreground"><span>{safePage} / {pages}</span><div className="flex gap-2"><Button size="sm" variant="outline" disabled={safePage <= 1} onClick={() => setPage((value) => value - 1)}>←</Button><Button size="sm" variant="outline" disabled={safePage >= pages} onClick={() => setPage((value) => value + 1)}>→</Button></div></div>
          </div>
        </>}

        {mode === "detail" && detailSource && <>
          <SheetHeader className="border-b px-4 py-5 sm:px-6">
            <Button variant="ghost" size="sm" className="mb-2 w-fit" onClick={() => { setDetailSource(null); setMode("list"); }}><ArrowLeft />{text.back}</Button>
            <SheetTitle className="break-all">{detailSource.fileName}</SheetTitle>
            <SheetDescription>{text.details}</SheetDescription>
          </SheetHeader>
          <div className="space-y-5 p-4 sm:p-6">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <MetaCard icon={<CalendarDays />} label={text.importedAt} value={formatDate(detailSource.createdAt, language)} />
              <MetaCard icon={<FileArchive />} label={text.importedBy} value={detailSource.importedByName} />
              <MetaCard icon={<HardDrive />} label={text.fileSize} value={formatSize(detailSource.fileSize, language)} />
              <MetaCard icon={<Filter />} label={text.rows} value={`${detailSource.detectedRowCount ?? detailSource.rowCount} · ${detailSource.insertedCount} ${text.inserted.toLowerCase()} · ${detailSource.excludedCount ?? "—"} ${text.excluded.toLowerCase()}`} />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => { onIsolate([detailSource.id]); onOpenChange(false); }}><Filter />{text.isolate}</Button>
              {canDownload && <Button variant="outline" disabled={!detailSource.storagePath || downloadBusyId === detailSource.id} onClick={() => void downloadSource(detailSource)}>{downloadBusyId === detailSource.id ? <LoaderCircle className="animate-spin" /> : <Download />}{text.download}</Button>}
              {canDelete && <Button variant="destructive" className="ml-auto" onClick={() => void openDelete([detailSource.id])}><Trash2 />{text.deleteSelected}</Button>}
            </div>
            <div className="rounded-xl border bg-muted/20 p-4"><p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{text.fileHash}</p><p className="mt-2 break-all font-mono text-xs">{detailSource.fileSha256}</p></div>
            <div><h3 className="mb-3 font-semibold">{text.rowsFromSource}</h3>{detailBusy ? <div className="flex min-h-40 items-center justify-center"><LoadingStatus label={language === "id" ? "Memuat baris sumber…" : "Loading source rows…"} /></div> : <><div className="hidden overflow-x-auto rounded-2xl border md:block"><Table className="min-w-[820px]"><TableHeader><TableRow className="bg-muted/40"><TableHead>#</TableHead><TableHead>{language === "id" ? "Tanggal" : "Date"}</TableHead><TableHead>{language === "id" ? "Referensi" : "Reference"}</TableHead><TableHead>{language === "id" ? "Deskripsi" : "Description"}</TableHead><TableHead>{language === "id" ? "Arah" : "Direction"}</TableHead><TableHead className="text-right">{language === "id" ? "Nominal" : "Amount"}</TableHead><TableHead>{text.raw}</TableHead></TableRow></TableHeader><TableBody>{visibleDetailRows.map((row) => { const linked = transactionById.get(row.transactionId); return <TableRow key={`${row.transactionId}-${row.rowNumber}`}><TableCell>{row.rowNumber}</TableCell><TableCell>{payloadText(row.sourcePayload, "transaction_date")}</TableCell><TableCell>{payloadText(row.sourcePayload, "payment_ref")}</TableCell><TableCell><button type="button" disabled={!linked} className="group block max-w-[260px] text-left disabled:cursor-default" onClick={() => linked && setDetailTransaction({ ...linked, sourceRowNumber: row.rowNumber })}><span className="block truncate font-medium group-enabled:group-hover:text-[#82305b]" title={payloadText(row.sourcePayload, "subject")}>{payloadText(row.sourcePayload, "subject")}</span>{linked && <span className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-[#82305b] dark:text-[#f1b9d5]"><Eye className="size-3" />{text.transactionDetails}</span>}</button></TableCell><TableCell>{payloadText(row.sourcePayload, "direction")}</TableCell><TableCell className="text-right font-medium tabular-nums">{payloadAmount(row.sourcePayload, language)}</TableCell><TableCell><details><summary className="cursor-pointer text-xs text-primary">JSON</summary><pre className="mt-2 max-h-48 max-w-[360px] overflow-auto rounded-lg bg-muted p-3 text-[10px]">{JSON.stringify(row.sourcePayload, null, 2)}</pre></details></TableCell></TableRow>; })}</TableBody></Table></div><div className="space-y-2 md:hidden">{visibleDetailRows.map((row) => { const linked = transactionById.get(row.transactionId); return <article key={`${row.transactionId}-${row.rowNumber}-mobile`} className="rounded-xl border p-4"><button type="button" disabled={!linked} className="group block w-full text-left disabled:cursor-default" onClick={() => linked && setDetailTransaction({ ...linked, sourceRowNumber: row.rowNumber })}><span className="flex items-start justify-between gap-3"><span className="text-xs text-muted-foreground">#{row.rowNumber} · {payloadText(row.sourcePayload, "transaction_date")}</span><span className="font-semibold tabular-nums">{payloadAmount(row.sourcePayload, language)}</span></span><span className="mt-2 block break-words text-sm font-medium group-enabled:group-hover:text-[#82305b]">{payloadText(row.sourcePayload, "subject")}</span><span className="mt-2 block break-all font-mono text-xs text-muted-foreground">{payloadText(row.sourcePayload, "payment_ref")}</span>{linked && <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[#82305b] dark:text-[#f1b9d5]"><Eye className="size-3.5" />{text.transactionDetails}</span>}</button><div className="mt-3 flex items-center justify-between"><Badge variant="secondary">{payloadText(row.sourcePayload, "direction")}</Badge><details><summary className="cursor-pointer text-xs text-primary">JSON</summary><pre className="mt-2 max-h-48 max-w-[280px] overflow-auto rounded-lg bg-muted p-3 text-[10px]">{JSON.stringify(row.sourcePayload, null, 2)}</pre></details></div></article>; })}</div></>}</div>
            <div className="flex items-center justify-between text-sm text-muted-foreground"><span>{detailSafePage} / {detailPages}</span><div className="flex gap-2"><Button size="sm" variant="outline" disabled={detailSafePage <= 1} onClick={() => setDetailPage((value) => value - 1)}>←</Button><Button size="sm" variant="outline" disabled={detailSafePage >= detailPages} onClick={() => setDetailPage((value) => value + 1)}>→</Button></div></div>
          </div>
        </>}

        {mode === "delete" && <>
          <SheetHeader className="border-b px-4 py-5 sm:px-6">
            <Button variant="ghost" size="sm" className="mb-2 w-fit" disabled={deleteBusy} onClick={() => setMode(detailSource && deleteIds.length === 1 && deleteIds[0] === detailSource.id ? "detail" : "list")}><ArrowLeft />{text.back}</Button>
            <SheetTitle className="flex items-center gap-2 text-destructive"><ShieldAlert className="size-5" />{text.deleteTitle}</SheetTitle>
            <SheetDescription>{text.deleteDescription}</SheetDescription>
          </SheetHeader>
          <div className="space-y-5 p-4 sm:p-6">
            {!deletePreview ? <div className="flex min-h-48 items-center justify-center"><LoadingStatus label={language === "id" ? "Menghitung dampak penghapusan…" : "Calculating deletion impact…"} /></div> : <>
              <div className="grid gap-3 sm:grid-cols-2">
                <ImpactCard value={deletePreview.batchCount} label={text.selectedFiles} danger />
                <ImpactCard value={deletePreview.transactionCount} label={text.affected} />
                <ImpactCard value={deletePreview.exclusiveTransactionCount} label={text.exclusive} danger />
                <ImpactCard value={deletePreview.sharedTransactionCount} label={text.shared} />
              </div>
              <p className="rounded-xl border bg-muted/25 p-4 text-sm"><strong>{deletePreview.originalFileCount}</strong> {text.originals}.</p>
              <fieldset className="space-y-3"><label className="flex cursor-pointer gap-3 rounded-xl border p-4"><input type="radio" name="delete-mode" checked={!deleteExclusive} onChange={() => setDeleteExclusive(false)} className="mt-1 accent-fuchsia-600" /><span className="text-sm font-medium">{text.keepTransactions}</span></label><label className="flex cursor-pointer gap-3 rounded-xl border border-destructive/30 bg-destructive/5 p-4"><input type="radio" name="delete-mode" checked={deleteExclusive} onChange={() => setDeleteExclusive(true)} className="mt-1 accent-red-600" /><span className="text-sm font-medium">{text.deleteTransactions}</span></label></fieldset>
              <label className="block"><span className="mb-2 block text-sm font-medium">{text.typeDelete}</span><Input value={deleteConfirmation} onChange={(event) => setDeleteConfirmation(event.target.value.toUpperCase())} autoComplete="off" placeholder={requiredConfirmation} /></label>
              <Button variant="destructive" className="min-h-11 w-full" disabled={deleteBusy || deleteConfirmation !== requiredConfirmation} onClick={() => void confirmDelete()}>{deleteBusy ? <LoaderCircle className="animate-spin" /> : <Trash2 />}{deleteBusy ? text.deleting : text.confirmDelete}</Button>
            </>}
          </div>
        </>}
      </SheetContent>
    </Sheet><TransactionDetailPreview transaction={detailTransaction} onOpenChange={(next) => { if (!next) setDetailTransaction(null); }} language={language} units={businessUnits} categories={categories} accounts={accounts} />
    </>
  );
}

function MetaCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return <div className="rounded-2xl border bg-card p-4"><span className="mb-3 flex size-9 items-center justify-center rounded-xl bg-muted text-primary [&_svg]:size-4">{icon}</span><p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</p><p className="mt-2 break-words text-sm font-medium">{value}</p></div>;
}

function ImpactCard({ value, label, danger = false }: { value: number; label: string; danger?: boolean }) {
  return <div className={`rounded-2xl border p-4 ${danger ? "border-destructive/30 bg-destructive/5" : "bg-card"}`}><p className={`text-2xl font-semibold tabular-nums ${danger ? "text-destructive" : ""}`}>{value}</p><p className="mt-1 text-sm text-muted-foreground">{label}</p></div>;
}
